import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Search, FileText, Sparkles, ArrowRight, X, Building2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { getJSON } from "../../lib/api";
import { useAuthStore } from "../../state/authStore";
import { useWizardClientLink, applyIpamPrefillToStore } from "../state/wizardPrefill";

const FREE_PLANS = new Set(["free", "trial"]);

/** Phase 7c.4 — entry overlay shown to Pro/MSP users when the wizard mounts
 *  without an active client link. Two paths: "Start from existing client"
 *  (picks an IPAM client and prefills) vs "Start blank" (no-op, current
 *  behaviour). Once the user makes a choice within a given session we stop
 *  asking — `dismissed` stays true until the page is reloaded.
 *
 *  Free users go straight to blank — the lead-gen funnel keeps working. */
export const WizardEntryPicker = ({ onResolved }) => {
    const user = useAuthStore((s) => s.user);
    const plan = (user?.subscription_plan || "free").toLowerCase();
    const isPaid = user && !FREE_PLANS.has(plan);
    const linkedClientId = useWizardClientLink((s) => s.client_id);

    // The picker only shows once per session — once the user has made a
    // choice (blank or a specific client) we don't nag again.
    const [dismissed, setDismissed] = useState(false);
    const open = Boolean(isPaid && !dismissed && !linkedClientId);

    const [clients, setClients] = useState([]);
    const [loadingClients, setLoadingClients] = useState(false);
    const [query, setQuery] = useState("");
    const [selectedClient, setSelectedClient] = useState(null);
    const [prefillPreview, setPrefillPreview] = useState(null);
    const [loadingPrefill, setLoadingPrefill] = useState(false);
    const [pickFirewall, setPickFirewall] = useState(null); // disambig dropdown
    const [applying, setApplying] = useState(false);

    useEffect(() => {
        if (!open) return;
        setLoadingClients(true);
        getJSON(`/ipam/clients?status=active&limit=50`)
            .then((data) => setClients(data.items || []))
            .catch(() => setClients([]))
            .finally(() => setLoadingClients(false));
    }, [open]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return clients.slice(0, 20);
        return clients.filter((c) =>
            (c.name || "").toLowerCase().includes(q) ||
            (c.slug || "").toLowerCase().includes(q) ||
            (c.industry || "").toLowerCase().includes(q),
        ).slice(0, 20);
    }, [clients, query]);

    const fetchPrefill = async (client) => {
        setSelectedClient(client);
        setPrefillPreview(null);
        setPickFirewall(null);
        setLoadingPrefill(true);
        try {
            const data = await getJSON(`/ipam/clients/${client.id}/wizard-prefill`);
            setPrefillPreview(data);
            if (data.firewalls && data.firewalls.length > 1) {
                setPickFirewall(data.firewalls[0].id);
            }
        } catch (e) {
            toast.error(e.message || "Couldn't load this client's data");
            setSelectedClient(null);
        } finally {
            setLoadingPrefill(false);
        }
    };

    const apply = async () => {
        if (!prefillPreview) return;
        setApplying(true);
        try {
            // If user picked a specific firewall, reorder so applyIpamPrefillToStore
            // sees that one first.
            let payload = prefillPreview;
            if (pickFirewall && prefillPreview.firewalls.length > 1) {
                const reordered = [...prefillPreview.firewalls].sort((a, b) =>
                    a.id === pickFirewall ? -1 : b.id === pickFirewall ? 1 : 0,
                );
                payload = { ...prefillPreview, firewalls: reordered };
            }
            const report = applyIpamPrefillToStore(payload);
            useWizardClientLink.getState().setClientLink(
                prefillPreview.client.id, prefillPreview.client.name,
            );
            // Single condensed success toast — the report.applied is for the
            // optional debug pane / details, but a noisy toast tower would be
            // worse UX than a single line.
            toast.success(
                `Loaded from ${prefillPreview.client.name}: ${report.applied.length} field(s) prefilled — feel free to edit`,
                { duration: 6000 },
            );
            report.warnings.forEach((w) => toast.warning(w, { duration: 7000 }));
            setDismissed(true);
            onResolved?.({ source: "from_client", client_id: prefillPreview.client.id });
        } finally {
            setApplying(false);
        }
    };

    const blank = () => {
        useWizardClientLink.getState().clearClientLink();
        setDismissed(true);
        onResolved?.({ source: "blank", client_id: null });
    };

    if (!open) return null;

    const counts = prefillPreview
        ? {
            subnets: Object.values(prefillPreview.subnets_by_purpose || {})
                .reduce((acc, l) => acc + (l?.length || 0), 0),
            firewalls: prefillPreview.firewalls?.length || 0,
            dns: prefillPreview.dns_candidates?.length || 0,
        }
        : null;

    return (
        <div
            className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-canvas/85 backdrop-blur-md"
            role="dialog"
            aria-modal="true"
            aria-label="Wizard entry picker"
            data-testid="wizard-entry-picker"
        >
            <div className="relative w-full max-w-2xl rounded-2xl border border-white/[0.08] bg-surface/95 shadow-heavy-popover p-7">
                <button
                    type="button"
                    onClick={blank}
                    aria-label="Skip — start blank"
                    className="absolute top-3 right-3 text-text-dim hover:text-text-primary p-1.5 rounded"
                    data-testid="wizard-entry-close"
                >
                    <X className="w-4 h-4" />
                </button>
                <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan">
                    ZeroConf wizard
                </p>
                <h2 className="mt-2 font-display font-semibold text-2xl text-text-primary tracking-tight">
                    How do you want to start?
                </h2>
                <p className="mt-1 text-[13px] text-text-muted">
                    Pick an existing client to prefill subnets, firewall model and DNS — or start from scratch.
                </p>

                <div className="mt-5 grid gap-4 md:grid-cols-2">
                    {/* LEFT — from client */}
                    <div className="rounded-xl border border-accent-cyan/30 bg-accent-cyan/[0.04] p-4">
                        <div className="flex items-center gap-2">
                            <Building2 className="w-3.5 h-3.5 text-accent-cyan" />
                            <span className="text-[11px] font-mono uppercase tracking-[0.18em] text-accent-cyan">
                                From existing client
                            </span>
                        </div>
                        <p className="mt-1 text-[12px] text-text-muted">
                            Pulls hardware, subnets and important IPs into the wizard.
                        </p>
                        <div className="relative mt-3">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-dim" />
                            <input
                                type="search"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder={loadingClients ? "Loading…" : "Search clients…"}
                                className="w-full pl-9 pr-3 py-2 rounded-md bg-elevated/60 border border-white/[0.06] text-text-primary text-[13px] focus:outline-none focus:border-accent-cyan/40"
                                data-testid="wizard-entry-client-search"
                            />
                        </div>
                        <div className="mt-2 max-h-44 overflow-y-auto rounded-md border border-white/[0.05]">
                            {filtered.length === 0 ? (
                                <div className="px-3 py-4 text-[12px] text-text-dim text-center">
                                    {loadingClients ? "Loading…" : "No clients yet — add one in IPAM first"}
                                </div>
                            ) : (
                                filtered.map((c) => (
                                    <button
                                        key={c.id}
                                        type="button"
                                        onClick={() => fetchPrefill(c)}
                                        className={`w-full text-left px-3 py-2 hover:bg-elevated/60 border-b border-white/[0.04] last:border-b-0 ${
                                            selectedClient?.id === c.id ? "bg-elevated/80" : ""
                                        }`}
                                        data-testid={`wizard-entry-client-${c.slug}`}
                                    >
                                        <div className="text-[13px] text-text-primary truncate">{c.name}</div>
                                        <div className="text-[11px] text-text-dim truncate">
                                            {c.industry || "—"}{c.subnets_count ? ` · ${c.subnets_count} subnets` : ""}
                                        </div>
                                    </button>
                                ))
                            )}
                        </div>
                        {selectedClient && (
                            <div className="mt-3 rounded-md border border-accent-cyan/30 bg-canvas/40 p-3" data-testid="wizard-entry-preview">
                                {loadingPrefill ? (
                                    <div className="flex items-center gap-2 text-text-muted text-[12px]">
                                        <Loader2 className="w-3 h-3 animate-spin" /> Loading data…
                                    </div>
                                ) : counts ? (
                                    <>
                                        <div className="text-[13px] text-text-primary">{selectedClient.name}</div>
                                        <div className="mt-1 text-[11.5px] text-text-muted">
                                            <span>{counts.firewalls} firewall{counts.firewalls !== 1 ? "s" : ""}</span>
                                            <span className="mx-2 text-text-dim">·</span>
                                            <span>{counts.subnets} subnet{counts.subnets !== 1 ? "s" : ""} mappable</span>
                                            <span className="mx-2 text-text-dim">·</span>
                                            <span>{counts.dns} DNS hint{counts.dns !== 1 ? "s" : ""}</span>
                                        </div>
                                        {counts.firewalls > 1 && (
                                            <div className="mt-3">
                                                <p className="text-[10.5px] font-mono uppercase tracking-[0.16em] text-accent-amber flex items-center gap-1">
                                                    <AlertTriangle className="w-3 h-3" /> Which firewall?
                                                </p>
                                                <select
                                                    value={pickFirewall || ""}
                                                    onChange={(e) => setPickFirewall(e.target.value)}
                                                    className="mt-1 w-full px-2 py-1.5 rounded bg-elevated/60 border border-white/[0.06] text-text-primary text-[12px]"
                                                    data-testid="wizard-entry-firewall-pick"
                                                >
                                                    {prefillPreview.firewalls.map((fw) => (
                                                        <option key={fw.id} value={fw.id}>
                                                            {fw.vendor} {fw.model}{fw.hostname ? ` · ${fw.hostname}` : ""}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}
                                        {counts.subnets === 0 && counts.firewalls === 0 && (
                                            <p className="mt-2 text-[11px] text-accent-amber">
                                                This client has no subnets or firewalls yet — picking it won't prefill anything.
                                            </p>
                                        )}
                                        <button
                                            type="button"
                                            onClick={apply}
                                            disabled={applying}
                                            className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-accent-cyan text-canvas text-[12px] font-medium hover:shadow-glow-cyan disabled:opacity-40"
                                            data-testid="wizard-entry-apply"
                                        >
                                            {applying ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                                            Continue with this client
                                        </button>
                                    </>
                                ) : null}
                            </div>
                        )}
                    </div>

                    {/* RIGHT — start blank */}
                    <div className="rounded-xl border border-white/[0.06] bg-canvas/40 p-4 flex flex-col">
                        <div className="flex items-center gap-2">
                            <FileText className="w-3.5 h-3.5 text-text-muted" />
                            <span className="text-[11px] font-mono uppercase tracking-[0.18em] text-text-muted">
                                Start blank
                            </span>
                        </div>
                        <p className="mt-1 text-[12px] text-text-muted">
                            No prefill. Pick vendor and configure everything yourself — same as before.
                        </p>
                        <div className="grow" />
                        <button
                            type="button"
                            onClick={blank}
                            className="mt-3 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md border border-white/[0.08] text-text-muted hover:text-text-primary hover:border-white/[0.15] text-[12px]"
                            data-testid="wizard-entry-blank"
                        >
                            Start blank <ArrowRight className="w-3 h-3" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WizardEntryPicker;
